import {
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  ViewEncapsulation,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BmarkClientOrdersTableDefinitionFactory,
  ProfilePromiseActionService,
  SystemConfigPromiseService,
  BfxBlotterAction,
  ProfileSettingKeys,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { ReplaySubject, Subject, Subscription } from "rxjs";
import { BfxBaseBlotter, provideBlotter } from "../../../bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterColumnSelectorService } from "../../../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../../../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxBlotterFilter } from "../../../bfx-blotter-filter/bfx-blotter-filter.constants";
import { BfxMouseModifierKeyService } from "../../../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "../../../bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "../../../bfx-common/services/bfx-user-profile.service";
import { GoldenLayoutService } from "../../../golden-layout/golden-layout-service";
import { BmarkClientOrdersContextMenuService } from "./services/bmark-client-orders-context-menu.service";
import { BmarkClientOrdersFilterService } from "./services/bmark-client-orders-filter.service";
import { BmarkClientOrdersDataService } from "./services/bmark-client-orders-data.service";
import { GridOptions } from "ag-grid-community/dist/lib/entities/gridOptions";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { OrderTypeRenderer } from "../../../bfx-blotter-orders/renderers/OrderTypeRenderer";
import { SpotTrackingRenderer } from "../../../bfx-blotter-orders/renderers/SpotTrackingRenderer";
import { MarketMidRenderer } from "../../../bfx-blotter-orders/renderers/MarketMidRenderer";
import { PercentAwayRenderer } from "../../../bfx-blotter-orders/renderers/PercentAwayRenderer";
import { BmarkClientOrdersBlotterMode } from "../../../../shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import { RowNode } from "ag-grid-community";
import { BmarkSummaryUtilService } from "../../services/bmark-summary-util.service";
import { MultiAllocProductRenderer } from "app/bfx-blotter-common/renderers/MultiAllocProductRenderer";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";
import { COLUMN_DEALT_DIR_DATA } from "shared/cubiq-services/model/BmarkClientOrdersTableDefinitionFactory.constants";

const CLIENT_ORDERS_PROFILE_KEY_SUFFIX = "bmarkClientOrders";
const BLOTTER_PREFERENCES_KEY = ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences;

@Component({
  selector: "bmark-client-orders-blotter",
  templateUrl: "./html/bmark-client-orders-blotter.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BmarkClientOrdersBlotterComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BmarkClientOrdersBlotterComponent
  extends BfxBaseBlotter
  implements AfterViewInit, OnDestroy
{
  protected static inMemoryReady: Subject<void> = new ReplaySubject<any>();
  protected static initialized: boolean = false;
  protected static primed: boolean = false;
  protected static refreshSubject: Subject<any> = new Subject<any>();
  protected static queuedRowData: Array<any> = [];
  protected static rawData: any = {};
  protected static refreshColumns: any = {};
  protected static transaction: any = {
    add: [],
    remove: [],
    update: [],
    pending: {},
    lastUpdate: 0,
  };

  public mode = OrdersTableMode.DEALT;

  protected get dealtMode(): boolean {
    // don't use in template
    return OrdersTableMode.DEALT === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  protected set dealtMode(isDealt: boolean) {
    if (isDealt === true && OrdersTableMode.BASE === this.mode) {
      this.mode = OrdersTableMode.DEALT;
    } else if (isDealt === true && OrdersTableMode.NDF_BASE === this.mode) {
      this.mode = OrdersTableMode.NDF_DEALT;
    } else if (OrdersTableMode.DEALT === this.mode) {
      this.mode = OrdersTableMode.BASE;
    } else if (OrdersTableMode.NDF_DEALT === this.mode) {
      this.mode = OrdersTableMode.NDF_BASE;
    }
  }

  private lastItemCounter: number = -1;
  public isFullTempStyle: boolean = true;

  private subscriptions: Subscription[] = [];
  private rendererSettings: { intentStylingEnabled: boolean };

  public frameworkComponents: any = {
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer,
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
    productRenderer: MultiAllocProductRenderer,
  };

  @Input()
  public orderIDsFromInput: any;

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BmarkClientOrdersDataService,
    protected _contextMenuServices: BmarkClientOrdersContextMenuService,
    zone: NgZone,
    _tableDefinitionFactory: BmarkClientOrdersTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    protected filteringService: BmarkClientOrdersFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected bmarkSummaryUtilService: BmarkSummaryUtilService,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuServices,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
    );

    this.blotterId = this.constructor.name;
    this.blotterInstance = this;

    this.subscriptions.push(
      this.bmarkSummaryUtilService.quickFilterSubject.subscribe(
        this.onQuickFilterChanged.bind(this),
      ),
    );

    this.profileActionService.getProfileConfig(BLOTTER_PREFERENCES_KEY).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.subscriptions.push(
      this.profileActionService
        .getProfileConfigUpdates(BLOTTER_PREFERENCES_KEY)
        .subscribe((profileValue) => {
          this.mapUserSettings(profileValue);
        }),
    );
  }

  ngAfterViewInit() {
    this.updateGridData();
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Extract the styling settings and add to the grid context
   * @param profileValue
   */
  private mapUserSettings(profileValue: any) {
    try {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        const intentStylingEnabled = savedSettings.intentStylingEnabled === "y";
        if (intentStylingEnabled != this.rendererSettings.intentStylingEnabled) {
          this.rendererSettings.intentStylingEnabled = intentStylingEnabled;
          this.refreshRendererSettings();
        }
      }
    } catch (e) {
      logger.out.warn(`bcob-c - settings error with value: 'profileValue' : ${e}`);
    }
  }

  /**
   * Refresh renderers that refernce changed context data
   */
  private refreshRendererSettings() {
    setTimeout(() => {
      logger.out.info("bcob-c - Refreshing intent col");
      this.gridOptions.api.refreshCells({
        columns: [COLUMN_DEALT_DIR_DATA],
        force: true,
      });
    }, 100);
  }

  /**
   * Override to add in the contextual settings values for renderers
   * @returns
   */
  protected override initGridOptionsContext(): any {
    let retContext = super.initGridOptionsContext();
    // Add a blotterSettings - this is called from constructor so do init here rather than as property initialiser
    this.rendererSettings = { intentStylingEnabled: false };
    retContext.rendererSettings = this.rendererSettings;
    return retContext;
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions: GridOptions = super.createsGridOptions();

    gridOptions.rowClassRules = {
      hot: this.checkHotStyle.bind(this),
      external: this.checkExternalStyle.bind(this),
      // not all of these acceptance states are currently in use in client orders
      // later specification may diverge colors from orders blotter
      cancel_acceptance_needed: function (params) {
        return CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      modify_acceptance_needed: function (params) {
        return CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      expiry_acceptance_needed: function (params) {
        return CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      migration_acceptance_needed: function (params) {
        return CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED == params.data.orderState;
      },
      acceptance_needed: function (params) {
        return CubiqTS.OrderState.ACCEPTANCE_NEEDED == params.data.orderState;
      },
      // not applicable to BMs:
      // 'trigger_expiry_acceptance_needed': function (params) { return CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED == params.data.orderState },
      // 'suspended': function (params) { return CubiqTS.OrderState.SUSPENDED === params.data.orderState },
      // OCO only:
      // 'pending_cancellation': function (params) { return CubiqTS.OrderState.PENDING_CANCELLATION == params.data.orderState },
      rowFlash: function (params) {
        return params.data.rowFlash;
      },
    };

    gridOptions.cacheQuickFilter = false;

    return gridOptions;
  }

  // protected initializeInMemory(): void {
  //   super.initializeInMemory();
  // }

  public saveAsDefault() {
    super.saveAsDefault();
  }

  protected getBadgeNumber(): number {
    return this.service.getItemCount();
  }

  /****************
   * click events *
   ****************/

  public onCellDoubleClicked(event) {
    if (
      event == Object(event) &&
      event.node === Object(event.node) &&
      typeof event.node.id === "string"
    ) {
      let id =
        event.node.data instanceof CubiqTS.OrderDataImpl
          ? event.node.data.orderId.toString()
          : event.node.id;
      this._contextMenuServices.fireActionEvent(
        BfxBlotterAction.ACTION_VIEW_DETAILS,
        this,
        [id],
        [event.node.data],
        null,
        null,
        null,
        event,
      );
    }
  }

  /**
   *  maybe needed later
   *
   * public showSettings(event: Event): void {
   *  this.layoutService.showSettings('Benchmark', event);
   *   }
   */

  /** HELPER FUNCTIONS **/

  private checkHotStyle(params): boolean {
    let isHot = false;

    switch (params.data.orderState) {
      case CubiqTS.OrderState.FILL_EXPIRED:
        isHot = true;
        break;
      case CubiqTS.OrderState.FILL_ERROR:
        isHot = true;
        break;
      default:
        break;
    }
    return isHot;
  }

  protected checkExternalStyle(params): boolean {
    return (
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED == params.data.orderState ||
      CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED == params.data.orderState ||
      CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED == params.data.orderState
    );
  }

  /**********************************************************************/
  /* BLOTTER - PROFILE */
  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  public getBlotterTypeStr(): string {
    return CLIENT_ORDERS_PROFILE_KEY_SUFFIX;
  }

  public filterAppliedEvent(): void {
    this.service.onFilterApplied();
  }

  protected afterFilteringCompleted(): void {
    this.updateGridData({ redrawRows: true }); // force it as the data wasn;t being updated inline.
  }

  protected onQuickFilterChanged(searchText: string): void {
    if (this.gridOptions != null && this.gridOptions.api != null) {
      this.gridOptions.api.setQuickFilter(searchText);
      setTimeout(this.updateQuickFilterData.bind(this), 0);
    }
  }

  protected updateQuickFilterData(): void {
    this.gridDataUpdated();
    this.service.onFilterApplied();
    this.bmarkSummaryUtilService.isFilterApplied(this.service.filterAppliedObj);
  }

  public doesExternalFilterPass(node: RowNode): boolean {
    let ret: boolean = true;
    if (this.filteringService && this.blotterFilterBar) {
      let filters: Array<BfxBlotterFilter> = this.blotterFilterBar.getSelectedFilters();

      try {
        if (
          (filters != null && filters.length > 0) ||
          (this.embeddedTableMode == false && this.filteringService.hasViewFilter())
        ) {
          ret = this.filteringService.doFiltersPass(node, filters, false, this.dealtMode);
        }
      } catch (error) {
        logger.out.error("Error while doing a filter pass.", error);
      }
    }

    return ret;
  }

  public isExternalFilterPresent(): boolean {
    if (this.filteringService.hasViewFilter()) {
      return true;
    }

    return super.isExternalFilterPresent();
  }
}
